import React, { useState, useEffect, useContext, useRef } from 'react';
import inventoryService from '../../services/inventoryService';
import api from '../../services/api';
import Modal from '../../comun/Modal';
import { AuthContext } from '../../context/AuthContext';

const Bodega = () => {
    const { user } = useContext(AuthContext);
    const roleName = user?.role_details?.name;
    const isAdmin = roleName === 'SUPER_ADMIN' || roleName === 'ADMIN_FAST_FOOD' || user?.is_superuser;


    const [activeTab, setActiveTab] = useState('en_uso'); // en_uso, bodega, instrumental, unidades, movimientos, recetas    
    // Estados generales
    const [supplies, setSupplies] = useState([]);
    const [movements, setMovements] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // Estado para Modales
    const [isSupplyModalOpen, setIsSupplyModalOpen] = useState(false);
    const [isMovementModalOpen, setIsMovementModalOpen] = useState(false);
    const [isRecipeModalOpen, setIsRecipeModalOpen] = useState(false);
    const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
    
    const fileInputRef = useRef(null);

    // Formularios
    const [editingSupply, setEditingSupply] = useState(null);
    const [supplyForm, setSupplyForm] = useState({ name: '', description: '', unit: '', current_stock: 0, is_active: true, is_production_item: false, is_auto_replenish: false, parent_supply: '', replenish_qty: 0, presentation: '', expiration_date: '', supply_type: 'en_uso' });
    const [unitOptions, setUnitOptions] = useState([]);
    
    // Unidades de medida
    const [units, setUnits] = useState([]);
    const [isUnitModalOpen, setIsUnitModalOpen] = useState(false);
    const [editingUnit, setEditingUnit] = useState(null);
    const [unitForm, setUnitForm] = useState({ name: '', abbreviation: '' });
    
    const [transferForm, setTransferForm] = useState({ supply: null, quantity: 1 });
    
    const [movementForm, setMovementForm] = useState({ supply: '', movement_type: 'in', quantity: 0, reason: '', lockedSupply: false });
    
    const [allRecipes, setAllRecipes] = useState([]);
    const [recipeForm, setRecipeForm] = useState({ id: null, product: '', supply: '', quantity: 0 });
    const [viewDetailsProductId, setViewDetailsProductId] = useState(null);

    // (Mezclas movido a su propia página /clinica/mezclas)

    const fetchData = async () => {
        setLoading(true);
        try {
            const [suppliesData, productsData, unitsData] = await Promise.all([
                inventoryService.getSupplies(),
                api.get('/api/menu/products/', { baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE }),
                inventoryService.getUnits()
            ]);
            setSupplies(suppliesData);
            setProducts(productsData.data?.results || productsData.data || []);
            
            setUnits(unitsData);
            const mappedUnits = unitsData.map(u => ({ value: u.id, display_name: `${u.name} (${u.abbreviation})` }));
            setUnitOptions(mappedUnits);
            if (mappedUnits.length > 0 && !supplyForm.unit) {
                setSupplyForm(prev => ({ ...prev, unit: mappedUnits[0].value }));
            }
            
            if (activeTab === 'movimientos') {
                const movs = await inventoryService.getMovements();
                setMovements(movs);
            }
            
            if (activeTab === 'recetas') {
                const recs = await inventoryService.getRecipeIngredients();
                setAllRecipes(recs);
            }
        } catch (error) {
            console.error("Error fetching bodega data:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [activeTab]);

    // ---- HANDLERS INSUMOS ----
    const handleSupplySubmit = async (e) => {
        e.preventDefault();
        try {
            const dataToSubmit = { ...supplyForm };
            if (!dataToSubmit.is_auto_replenish) {
                dataToSubmit.parent_supply = null;
                dataToSubmit.replenish_qty = 0;
            }
            
            if (editingSupply) {
                await inventoryService.updateSupply(editingSupply.id, dataToSubmit);
            } else {
                const stockToSet = parseFloat(supplyForm.current_stock) || 0;
                const newSupply = await inventoryService.createSupply({ ...dataToSubmit, current_stock: 0 }); // create with 0
                if (stockToSet > 0) {
                    await inventoryService.recordMovement(newSupply.id, {
                        movement_type: 'in',
                        quantity: stockToSet,
                        reason: 'Stock inicial'
                    });
                }
            }
            setIsSupplyModalOpen(false);
            fetchData();
        } catch (error) {
            alert('Error al guardar insumo');
        }
    };

    const handleDeleteSupply = async (id) => {
        if(window.confirm('¿Eliminar este insumo? Se eliminarán también sus movimientos.')) {
            try {
                await inventoryService.deleteSupply(id);
                fetchData();
            } catch (error) {
                alert('Error al eliminar insumo');
            }
        }
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('file', file);

        try {
            setLoading(true);
            const response = await inventoryService.importSuppliesExcel(formData);
            alert(`Importación exitosa. Nuevos: ${response.created}, Actualizados: ${response.updated}`);
            fetchData();
        } catch (error) {
            alert('Error al importar Excel: ' + (error.response?.data?.error || error.message));
        } finally {
            setLoading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    // ---- HANDLERS UNIDADES ----
    const handleUnitSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingUnit) {
                await inventoryService.updateUnit(editingUnit.id, unitForm);
            } else {
                await inventoryService.createUnit(unitForm);
            }
            setIsUnitModalOpen(false);
            fetchData();
        } catch (error) {
            alert('Error al guardar unidad');
        }
    };

    const handleDeleteUnit = async (id) => {
        if(window.confirm('¿Eliminar esta unidad de medida? No se podrá eliminar si está en uso.')) {
            try {
                await inventoryService.deleteUnit(id);
                fetchData();
            } catch (error) {
                alert('No se pudo eliminar. Es posible que esté asignada a insumos.');
            }
        }
    };

    // ---- HANDLERS MOVIMIENTOS ----
    const handleMovementSubmit = async (e) => {
        e.preventDefault();
        try {
            await inventoryService.recordMovement(movementForm.supply, {
                movement_type: movementForm.movement_type,
                quantity: parseFloat(movementForm.quantity),
                reason: movementForm.reason
            });
            setIsMovementModalOpen(false);
            fetchData();
        } catch (error) {
            alert('Error al registrar movimiento. Verifique los datos.');
        }
    };

    // ---- HANDLERS RECETAS ----
    const loadAllRecipes = async () => {
        try {
            const data = await inventoryService.getRecipeIngredients();
            setAllRecipes(data);
        } catch (error) {
            console.error(error);
        }
    };

    const handleRecipeSubmit = async (e) => {
        e.preventDefault();
        try {
            if (recipeForm.id) {
                await inventoryService.deleteRecipeIngredient(recipeForm.id);
            }
            await inventoryService.createRecipeIngredient({
                product: recipeForm.product,
                supply: recipeForm.supply,
                quantity: parseFloat(recipeForm.quantity),
                size: null
            });
            setIsRecipeModalOpen(false);
            loadAllRecipes();
        } catch (error) {
            console.error(error.response?.data || error);
            const msg = error.response?.data ? JSON.stringify(error.response.data) : error.message;
            alert('Error al guardar receta: ' + msg);
        }
    };
    
    const handleDeleteRecipe = async (id) => {
        if(window.confirm('¿Eliminar esta receta?')) {
            try {
                await inventoryService.deleteRecipeIngredient(id);
                loadAllRecipes();
            } catch (error) {
                alert('Error al eliminar');
            }
        }
    }


    const getStockDisplay = (supplyId) => {
        const s = supplies.find(sp => sp.id === supplyId);
        if (!s) return { stock: 0, unit: '', color: 'gray' };
        return {
            stock: s.current_stock,
            unit: s.unit_display,
            color: s.current_stock <= 0 ? 'red' : 'green'
        };
    };

    const handleTransferToUse = async (e) => {
        e.preventDefault();
        try {
            await inventoryService.transferToUse(transferForm.supply, parseFloat(transferForm.quantity));
            setIsTransferModalOpen(false);
            fetchData();
        } catch (error) {
            console.error(error);
            alert('Error al transferir a uso. Verifique la cantidad.');
        }
    };

    if (loading && supplies.length === 0) return <div>Cargando Bodega...</div>;

    const getExpirationColor = (dateString) => {
        if (!dateString) return 'transparent';
        const expDate = new Date(dateString);
        const today = new Date();
        const diffMonths = (expDate.getFullYear() - today.getFullYear()) * 12 + (expDate.getMonth() - today.getMonth());
        
        if (diffMonths <= 5) return 'rgba(255, 0, 0, 0.1)';
        if (diffMonths >= 6 && diffMonths <= 12) return 'rgba(255, 255, 0, 0.2)';
        return 'rgba(0, 255, 0, 0.1)';
    };

    return (
        <div className="page-container">
            <div className="page-header">
                <h2>Bodega e Insumos</h2>
            </div>

            <div className="tabs" style={{ marginBottom: '20px', borderBottom: '1px solid #e2e8f0', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <button className={`btn ${activeTab === 'en_uso' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setActiveTab('en_uso')}>Insumos en Uso</button>
                <button className={`btn ${activeTab === 'bodega' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setActiveTab('bodega')}>Insumos en Bodega</button>
                <button className={`btn ${activeTab === 'instrumental' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setActiveTab('instrumental')}>Instrumental</button>
                {isAdmin && <button className={`btn ${activeTab === 'unidades' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setActiveTab('unidades')}>Unidades</button>}
                {isAdmin && <button className={`btn ${activeTab === 'movimientos' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setActiveTab('movimientos')}>Movimientos</button>}
                
            </div>

            {/* TABS DE INVENTARIO */}
            {['en_uso', 'bodega', 'instrumental'].includes(activeTab) && (
                <div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem', gap: '1rem' }}>
                        <input 
                            type="file" 
                            accept=".xlsx, .xls" 
                            style={{ display: 'none' }} 
                            ref={fileInputRef} 
                            onChange={handleFileUpload} 
                        />
                        <button className="btn btn-secondary" onClick={() => fileInputRef.current?.click()}>
                            Importar Excel
                        </button>
                        <button className="btn btn-primary" onClick={() => {
                            setEditingSupply(null);
                            setSupplyForm({ name: '', description: '', unit: unitOptions.length ? unitOptions[0].value : '', current_stock: 0, is_active: true, is_production_item: false, is_auto_replenish: false, parent_supply: '', replenish_qty: 0, presentation: '', expiration_date: '', supply_type: activeTab });
                            setIsSupplyModalOpen(true);
                        }}>Nuevo {activeTab === 'instrumental' ? 'Instrumento' : 'Insumo'}</button>
                    </div>
                    
                    <div className="table-responsive">
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>Nombre</th>
                                    {activeTab !== 'instrumental' && <th>Presentación</th>}
                                    <th>Unidad</th>
                                    {isAdmin && <th>Stock Actual</th>}
                                    {activeTab !== 'instrumental' && <th>Caducidad</th>}
                                    <th>Estado</th>
                                    <th>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {supplies.filter(s => s.supply_type === activeTab).length === 0 ? <tr><td colSpan="7">No hay registros</td></tr> : supplies.filter(s => s.supply_type === activeTab).map(s => (
                                    <tr key={s.id} style={{ backgroundColor: getExpirationColor(s.expiration_date) }}>
                                        <td>{s.name}</td>
                                        {activeTab !== 'instrumental' && <td>{s.presentation || '-'}</td>}
                                        <td>{s.unit_display}</td>
                                        {isAdmin && <td style={{ fontWeight: 'bold', color: s.current_stock <= 0 ? 'red' : 'green'}}>{Math.round(s.current_stock)}</td>}
                                        {activeTab !== 'instrumental' && <td>{s.expiration_date ? s.expiration_date.split('-').reverse().join('/') : '-'}</td>}
                                        <td>
                                            {s.is_active ? 'Activo' : 'Inactivo'}
                                            {s.is_production_item && <span style={{ marginLeft: '4px', fontSize: '11px', background: '#0d6efd', color: '#fff', padding: '1px 6px', borderRadius: '8px' }}>Mezcla</span>}
                                        </td>
                                        <td>
                                            <button className="btn btn-secondary" style={{ marginRight: '5px', padding: '0.25rem' }} onClick={() => {
                                                setEditingSupply(s);
                                                setSupplyForm({ name: s.name, description: s.description, unit: s.unit, current_stock: Math.round(Number(s.current_stock)), is_active: s.is_active, is_production_item: s.is_production_item || false, is_auto_replenish: !!s.parent_supply, parent_supply: s.parent_supply || '', replenish_qty: s.replenish_qty || 0, presentation: s.presentation || '', expiration_date: s.expiration_date || '', supply_type: s.supply_type });
                                                setIsSupplyModalOpen(true);
                                            }}>Editar</button>
                                            
                                            {isAdmin && (
                                                <button className="btn btn-danger" style={{ marginRight: '5px', padding: '0.25rem' }} onClick={() => handleDeleteSupply(s.id)}>Eliminar</button>
                                            )}
                                            
                                            <button className="btn btn-success" style={{ marginRight: '5px', padding: '0.25rem' }} title="Agregar Stock" onClick={() => {
                                                setMovementForm({ supply: s.id, movement_type: 'in', quantity: 0, reason: '', lockedSupply: true });
                                                setIsMovementModalOpen(true);
                                            }}><i className="bi bi-arrow-up"></i></button>
                                            
                                            {isAdmin && (
                                                <button className="btn btn-warning" style={{ marginRight: '5px', padding: '0.25rem' }} title="Reducir Stock" onClick={() => {
                                                    setMovementForm({ supply: s.id, movement_type: 'out', quantity: 0, reason: '', lockedSupply: true });
                                                    setIsMovementModalOpen(true);
                                                }}><i className="bi bi-arrow-down"></i></button>
                                            )}

                                            {activeTab === 'bodega' && s.current_stock > 0 && (
                                                <button className="btn btn-primary" style={{ padding: '0.25rem' }} onClick={() => {
                                                    setTransferForm({ supply: s.id, quantity: 1 });
                                                    setIsTransferModalOpen(true);
                                                }}>Transferir a Uso</button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
            
            {activeTab === 'unidades' && isAdmin && (
                <div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
                        <button className="btn btn-primary" onClick={() => {
                            setEditingUnit(null);
                            setUnitForm({ name: '', abbreviation: '' });
                            setIsUnitModalOpen(true);
                        }}>Nueva Unidad</button>
                    </div>
                    
                    <div className="table-responsive">
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>Nombre</th>
                                    <th>Abreviatura</th>
                                    <th>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {units.length === 0 ? <tr><td colSpan="3">No hay unidades de medida</td></tr> : units.map(u => (
                                    <tr key={u.id}>
                                        <td>{u.name}</td>
                                        <td>{u.abbreviation}</td>
                                        <td>
                                            <button className="btn btn-secondary" style={{ marginRight: '5px', padding: '0.25rem' }} onClick={() => {
                                                setEditingUnit(u);
                                                setUnitForm({ name: u.name, abbreviation: u.abbreviation });
                                                setIsUnitModalOpen(true);
                                            }}>Editar</button>
                                            <button className="btn btn-danger" style={{ padding: '0.25rem' }} onClick={() => handleDeleteUnit(u.id)}>Eliminar</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* TAB MOVIMIENTOS */}
            {activeTab === 'movimientos' && (
                <div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
                        <button className="btn btn-primary" onClick={() => {
                            setMovementForm({ supply: supplies[0]?.id || '', movement_type: 'in', quantity: 0, reason: '', lockedSupply: false });
                            setIsMovementModalOpen(true);
                        }}>Registrar Movimiento</button>
                    </div>
                    
                    <div className="table-responsive">
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>Fecha</th>
                                    <th>Insumo</th>
                                    <th>Tipo</th>
                                    <th>Cantidad</th>
                                    <th>Motivo / Ref</th>
                                    <th>Usuario</th>
                                </tr>
                            </thead>
                            <tbody>
                                {movements.length === 0 ? <tr><td colSpan="6">No hay movimientos</td></tr> : movements.map(m => (
                                    <tr key={m.id}>
                                        <td>{new Date(m.created_at).toLocaleString()}</td>
                                        <td>{m.supply_name}</td>
                                        <td>
                                            <span className={`tag ${m.movement_type === 'in' ? 'tag-green' : m.movement_type === 'out' ? 'tag-red' : 'tag-gray'}`}>
                                                {m.movement_type_display}
                                            </span>
                                        </td>
                                        <td style={{ fontWeight: 'bold', color: m.movement_type === 'in' ? 'green' : (m.movement_type === 'out' || m.movement_type === 'sale' ? 'red' : 'inherit') }}>
                                            {m.movement_type === 'in' ? '+' : (m.movement_type === 'out' || m.movement_type === 'sale' ? '-' : '')}{Math.round(m.quantity)}
                                        </td>
                                        <td>{m.reason} {m.reference_id && `(Ref: ${m.reference_id})`}</td>
                                        <td>{m.created_by}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* MODALS */}
            {/* Modal Insumo */}
            <Modal isOpen={isSupplyModalOpen} onClose={() => setIsSupplyModalOpen(false)} title={editingSupply ? 'Editar Insumo' : 'Nuevo Insumo'}>
                <form onSubmit={handleSupplySubmit}>
                    <div className="form-group">
                        <label>Nombre del Insumo</label>
                        <input type="text" required value={supplyForm.name} onChange={e => setSupplyForm({...supplyForm, name: e.target.value})} />
                    </div>
                    <div className="form-group">
                        <label>Unidad de Medida</label>
                        <select className="form-control" value={supplyForm.unit} onChange={e => setSupplyForm({...supplyForm, unit: e.target.value})}>
                            {unitOptions.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.display_name}</option>
                            ))}
                        </select>
                    </div>
                    {supplyForm.supply_type !== 'instrumental' && (
                        <>
                            <div className="form-group">
                                <label>Presentación (Opcional)</label>
                                <input type="text" value={supplyForm.presentation} onChange={e => setSupplyForm({...supplyForm, presentation: e.target.value})} placeholder="Ej. Caja 50 un, Frasco 100ml" />
                            </div>
                            <div className="form-group">
                                <label>Fecha de Caducidad (Opcional)</label>
                                <input type="date" value={supplyForm.expiration_date} onChange={e => setSupplyForm({...supplyForm, expiration_date: e.target.value})} />
                            </div>
                        </>
                    )}
                    <div className="form-group">
                        <label>Stock Inicial</label>
                        <input type="number" step="1" required value={supplyForm.current_stock} onChange={e => setSupplyForm({...supplyForm, current_stock: e.target.value})} />
                    </div>
                    
                    <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '1rem' }}>
                        <input type="checkbox" id="is_auto_replenish"
                            checked={supplyForm.is_auto_replenish}
                            onChange={e => setSupplyForm({...supplyForm, is_auto_replenish: e.target.checked})} />
                        <label htmlFor="is_auto_replenish" style={{ margin: 0, fontWeight: 'bold' }}>
                            Es Descorche Automático (Auto-Recarga)
                        </label>
                    </div>
                    
                    {supplyForm.is_auto_replenish && (
                        <div style={{ padding: '10px', background: '#f8f9fa', borderLeft: '3px solid #0d6efd', marginBottom: '15px' }}>
                            <div className="form-group">
                                <label>Insumo Padre (a descontar)</label>
                                <select className="form-control" required value={supplyForm.parent_supply} onChange={e => setSupplyForm({...supplyForm, parent_supply: e.target.value})}>
                                    <option value="">Seleccione el insumo origen...</option>
                                    {supplies.filter(s => s.id !== editingSupply?.id).map(opt => (
                                        <option key={opt.id} value={opt.id}>{opt.name} ({opt.unit_display})</option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Cantidad de recarga (Ej: 35 Onzas por botella)</label>
                                <input type="number" step="0.001" min="0.001" required value={supplyForm.replenish_qty} onChange={e => setSupplyForm({...supplyForm, replenish_qty: e.target.value})} className="form-control" />
                            </div>
                        </div>
                    )}
                    
                    <div className="form-actions">
                        <button type="button" className="btn btn-secondary" onClick={() => setIsSupplyModalOpen(false)}>Cancelar</button>
                        <button type="submit" className="btn btn-primary">Guardar</button>
                    </div>
                </form>
            </Modal>

            {/* Modal Movimiento */}
            <Modal isOpen={isMovementModalOpen} onClose={() => setIsMovementModalOpen(false)} title="Registrar Movimiento de Bodega">
                <form onSubmit={handleMovementSubmit}>
                    <div className="form-group">
                        <label>Insumo</label>
                        <select className="form-control" required value={movementForm.supply} onChange={e => setMovementForm({...movementForm, supply: e.target.value})} disabled={movementForm.lockedSupply}>
                            {supplies.map(s => <option key={s.id} value={s.id}>{s.name} {isAdmin ? `(Stock actual: ${s.current_stock})` : ''}</option>)}
                        </select>
                    </div>
                    <div className="form-group">
                        <label>Tipo de Movimiento</label>
                        <select className="form-control" value={movementForm.movement_type} onChange={e => setMovementForm({...movementForm, movement_type: e.target.value})}>
                            <option value="in">Ingreso (Sumar al stock)</option>
                            {isAdmin && (
                                <>
                                    <option value="out">Egreso (Restar al stock)</option>
                                    <option value="adjustment">Ajuste (Reemplazar stock exacto)</option>
                                </>
                            )}
                        </select>
                    </div>
                    <div className="form-group">
                        <label>Cantidad</label>
                        <input type="number" step="1" required value={movementForm.quantity} onChange={e => setMovementForm({...movementForm, quantity: e.target.value})} />
                    </div>
                    <div className="form-group">
                        <label>Motivo</label>
                        <input type="text" required value={movementForm.reason} onChange={e => setMovementForm({...movementForm, reason: e.target.value})} placeholder="Ej: Compra a proveedor, daño..." />
                    </div>
                    <div className="form-actions">
                        <button type="button" className="btn btn-secondary" onClick={() => setIsMovementModalOpen(false)}>Cancelar</button>
                        <button type="submit" className="btn btn-primary">Registrar</button>
                    </div>
                </form>
            </Modal>

            {/* Modal Detalles Receta */}
            {viewDetailsProductId && (() => {
                const viewDetailsProduct = [...new Set(allRecipes.map(r => r.product))]
                    .map(productId => ({
                        product: productId,
                        product_name: allRecipes.find(r => r.product === productId).product_name,
                        recipes: allRecipes.filter(r => r.product === productId)
                    })).find(p => p.product === viewDetailsProductId);

                if (!viewDetailsProduct) return null;

                return (
                    <Modal isOpen={true} onClose={() => setViewDetailsProductId(null)} title={`Detalles de Receta: ${viewDetailsProduct.product_name}`} maxWidth="800px">
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '10px' }}>
                            <button className="btn btn-primary" onClick={() => {
                                setRecipeForm({ id: null, product: viewDetailsProduct.product, supply: supplies[0]?.id || '', quantity: 0 });
                                setIsRecipeModalOpen(true);
                            }}>Agregar Insumo a esta Receta</button>
                        </div>
                        <div className="table-responsive">
                            <table className="table">
                                <thead>
                                    <tr>
                                        <th>Insumo</th>
                                        <th>Cantidad</th>
                                        <th>Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {viewDetailsProduct.recipes.map(r => (
                                        <tr key={r.id}>
                                            <td>{r.supply_name}</td>
                                            <td>{Math.round(r.quantity)} {r.supply_unit}</td>
                                            <td>
                                                <button className="btn btn-secondary" style={{ marginRight: '5px', padding: '0.25rem' }} onClick={() => {
                                                    setRecipeForm({ id: r.id, product: r.product, supply: r.supply, quantity: r.quantity });
                                                    setIsRecipeModalOpen(true);
                                                }}>Editar</button>
                                                <button className="btn btn-danger" style={{ padding: '0.25rem' }} onClick={() => handleDeleteRecipe(r.id)}>Eliminar</button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Modal>
                );
            })()}

            {/* Modal Receta */}
            <Modal isOpen={isRecipeModalOpen} onClose={() => setIsRecipeModalOpen(false)} title={recipeForm.id ? "Editar Receta" : "Nueva Receta"}>
                <form onSubmit={handleRecipeSubmit}>
                    <div className="form-group">
                        <label>Producto del Menú</label>
                        <select className="form-control" required value={recipeForm.product} onChange={e => setRecipeForm({...recipeForm, product: e.target.value})}>
                            {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                    </div>
                    <div className="form-group">
                        <label>Insumo a descontar</label>
                        <select className="form-control" required value={recipeForm.supply} onChange={e => setRecipeForm({...recipeForm, supply: e.target.value})}>
                            {supplies.map(s => <option key={s.id} value={s.id}>{s.name} ({s.unit_display})</option>)}
                        </select>
                    </div>
                    <div className="form-group">
                        <label>Cantidad a descontar por cada venta</label>
                        <input type="number" step="1" required value={recipeForm.quantity} onChange={e => setRecipeForm({...recipeForm, quantity: e.target.value})} />
                    </div>
                    <div className="form-actions">
                        <button type="button" className="btn btn-secondary" onClick={() => setIsRecipeModalOpen(false)}>Cancelar</button>
                        <button type="submit" className="btn btn-primary">Guardar</button>
                    </div>
                </form>
            </Modal>

            {/* Modal Transferencia a Uso */}
            <Modal isOpen={isTransferModalOpen} onClose={() => setIsTransferModalOpen(false)} title="Transferir a Uso">
                <form onSubmit={handleTransferToUse}>
                    <div className="form-group">
                        <label>Cantidad a transferir de bodega a en uso</label>
                        <input type="number" step="1" min="1" max={supplies.find(s => s.id === transferForm.supply)?.current_stock || 1} required value={transferForm.quantity} onChange={e => setTransferForm({...transferForm, quantity: e.target.value})} className="form-control" />
                    </div>
                    <div className="form-actions">
                        <button type="button" className="btn btn-secondary" onClick={() => setIsTransferModalOpen(false)}>Cancelar</button>
                        <button type="submit" className="btn btn-primary">Transferir</button>
                    </div>
                </form>
            </Modal>
            
            {/* Modal Unidad de Medida */}
            <Modal isOpen={isUnitModalOpen} onClose={() => setIsUnitModalOpen(false)} title={editingUnit ? 'Editar Unidad' : 'Nueva Unidad'}>
                <form onSubmit={handleUnitSubmit}>
                    <div className="form-group">
                        <label>Nombre de la unidad</label>
                        <input type="text" required value={unitForm.name} onChange={e => setUnitForm({...unitForm, name: e.target.value})} placeholder="Ej: Kilogramo" />
                    </div>
                    <div className="form-group">
                        <label>Abreviatura</label>
                        <input type="text" required value={unitForm.abbreviation} onChange={e => setUnitForm({...unitForm, abbreviation: e.target.value})} placeholder="Ej: kg" />
                    </div>
                    <div className="form-actions">
                        <button type="button" className="btn btn-secondary" onClick={() => setIsUnitModalOpen(false)}>Cancelar</button>
                        <button type="submit" className="btn btn-primary">Guardar</button>
                    </div>
                </form>
            </Modal>
        </div>
    );
};

export default Bodega;
